import { useCallback, useEffect, useRef, useState } from 'react';

import type { SharedStudioState } from '../api';

const DEFAULT_DEBOUNCE_MS = 600;
const pendingProjectWrites = new Map<number, Promise<void>>();

/** Waits for scheduled writes, including an unmounted room's queued retry. */
export async function awaitPendingSharedStateSave(identity: number): Promise<void> {
  let pending = pendingProjectWrites.get(identity);
  while (pending) {
    await pending;
    pending = pendingProjectWrites.get(identity);
  }
}

function registerProjectWrite(identity: number) {
  const preceding = pendingProjectWrites.get(identity) ?? Promise.resolve();
  let finish!: () => void;
  const settled = new Promise<void>((resolve) => { finish = resolve; });
  const completion = preceding.then(() => settled);
  pendingProjectWrites.set(identity, completion);
  void completion.then(() => {
    if (pendingProjectWrites.get(identity) === completion) {
      pendingProjectWrites.delete(identity);
    }
  });
  return { preceding, finish };
}

interface SaveCandidate {
  identity: number;
  state: SharedStudioState;
  signature: string;
  sequence: number;
  generation: number;
  retryAttempt: number;
  save: UseSharedStateAutosaveOptions['save'];
  terminalFailure?: boolean;
  preceding: Promise<void>;
  finish: () => void;
}

interface UseSharedStateAutosaveOptions {
  identity: number | null;
  save: (identity: number, state: SharedStudioState) => Promise<SharedStudioState>;
  debounceMs?: number;
  retryDelayMs?: number;
  maxRetries?: number;
  onPersisted?: (state: SharedStudioState) => void;
  onError?: (error: unknown) => void;
}

interface SharedStateAutosave {
  isPending: boolean;
  markIntent: () => void;
  schedule: (state: SharedStudioState) => boolean;
  setPersistedState: (state: SharedStudioState | null) => void;
}

function getSharedTrackSettingsSignature(
  trackSettings: SharedStudioState['track_settings'],
) {
  return Object.fromEntries(
    Object.entries(trackSettings ?? {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, settings]) => [
        name,
        {
          volume: settings?.volume ?? 1,
          pan: settings?.pan ?? 0,
          muted: Boolean(settings?.muted),
        },
      ]),
  );
}

export function getSharedStateSignature(
  state: SharedStudioState | null | undefined,
): string {
  return JSON.stringify({
    loop_start_sec: state?.loop_start_sec ?? null,
    loop_end_sec: state?.loop_end_sec ?? null,
    bpm_ratio: state?.bpm_ratio ?? 1,
    pitch_semitones: state?.pitch_semitones ?? 0,
    selected_tab_id: state?.selected_tab_id ?? null,
    selected_tab_renderer: state?.selected_tab_renderer ?? null,
    track_settings: getSharedTrackSettingsSignature(state?.track_settings ?? {}),
  });
}

/**
 * Debounces shared-studio writes while keeping at most one PUT in flight.
 *
 * `serverSignatureRef` records the last response known to have reached the
 * server. `latestSequenceRef` records the newest local intent. An older
 * response may advance the server signature so a reverted value is not
 * incorrectly deduplicated, but it can only publish `onPersisted` when its
 * sequence (or a coalesced matching candidate) is still the latest intent.
 *
 * Leaving an identity flushes its already-scheduled candidate, even on
 * unmount. These writes retain their original save function and wait for the
 * current physical request; their responses cannot publish into another UI.
 */
export function useSharedStateAutosave({
  identity,
  save,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  retryDelayMs = 1_000,
  maxRetries = 2,
  onPersisted,
  onError,
}: UseSharedStateAutosaveOptions): SharedStateAutosave {
  const [isPending, setIsPending] = useState(false);
  const mountedRef = useRef(true);
  const identityRef = useRef<number | null>(identity);
  const generationRef = useRef(0);
  const nextSequenceRef = useRef(0);
  const latestSequenceRef = useRef(0);
  const unscheduledSequenceRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<SaveCandidate | null>(null);
  const departingRef = useRef<SaveCandidate[]>([]);
  const inFlightRef = useRef<SaveCandidate | null>(null);
  const serverSignatureRef = useRef<string | null>(null);
  const saveRef = useRef(save);
  const debounceMsRef = useRef(debounceMs);
  const retryDelayMsRef = useRef(retryDelayMs);
  const maxRetriesRef = useRef(maxRetries);
  const callbacksRef = useRef({ onPersisted, onError });

  saveRef.current = save;
  debounceMsRef.current = debounceMs;
  retryDelayMsRef.current = retryDelayMs;
  maxRetriesRef.current = maxRetries;
  callbacksRef.current = { onPersisted, onError };

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPendingSave = useCallback((): void => {
    if (inFlightRef.current) return;

    const candidate = departingRef.current.shift() ?? pendingRef.current;
    if (!candidate) return;
    if (pendingRef.current === candidate) pendingRef.current = null;
    const isCurrent = () => mountedRef.current
      && candidate.generation === generationRef.current
      && candidate.identity === identityRef.current;

    if (isCurrent() && candidate.signature === serverSignatureRef.current) {
      candidate.finish();
      if (candidate.sequence === latestSequenceRef.current) {
        callbacksRef.current.onPersisted?.(candidate.state);
      }
      return;
    }

    inFlightRef.current = candidate;
    if (mountedRef.current) setIsPending(true);

    let continueWithPending = false;
    let retryAfterMs: number | null = null;
    candidate.preceding
      .then(() => candidate.save(candidate.identity, candidate.state))
      .then((savedState) => {
        const savedSignature = getSharedStateSignature(savedState);
        departingRef.current = departingRef.current.filter((pending) => {
          if (pending.generation !== candidate.generation
            || pending.identity !== candidate.identity
            || pending.signature !== savedSignature) return true;
          pending.finish();
          return false;
        });
        if (!isCurrent()) return;

        serverSignatureRef.current = savedSignature;

        const pending = pendingRef.current;
        if (
          pending
          && pending.generation === candidate.generation
          && pending.identity === candidate.identity
          && pending.signature === savedSignature
        ) {
          pending.finish();
          pendingRef.current = null;
          if (pending.sequence === latestSequenceRef.current) {
            callbacksRef.current.onPersisted?.(savedState);
          }
        } else if (
          !pending
          && candidate.sequence === latestSequenceRef.current
        ) {
          callbacksRef.current.onPersisted?.(savedState);
        }
        continueWithPending = pendingRef.current !== null;
      })
      .catch((error: unknown) => {
        const pending = pendingRef.current;
        const hasNewer = [pending, ...departingRef.current].some((next) => next
          && next.identity === candidate.identity
          && next.sequence > candidate.sequence);
        if (hasNewer) {
          continueWithPending = true;
          return;
        }

        if (candidate.retryAttempt < maxRetriesRef.current) {
          const retry = {
            ...candidate,
            retryAttempt: candidate.retryAttempt + 1,
          };
          if (isCurrent()) pendingRef.current = retry;
          else departingRef.current.unshift(retry);
          retryAfterMs = retryDelayMsRef.current * (2 ** candidate.retryAttempt);
        } else if (isCurrent()) {
          pendingRef.current = { ...candidate, terminalFailure: true };
          callbacksRef.current.onError?.(error);
        }
      })
      .finally(() => {
        if (retryAfterMs == null) candidate.finish();
        if (inFlightRef.current !== candidate) return;
        inFlightRef.current = null;
        if (mountedRef.current) setIsPending(false);

        const pending = departingRef.current[0] ?? pendingRef.current;
        if (!pending || pending.terminalFailure) return;
        if (
          retryAfterMs == null && (
            pending.generation !== candidate.generation
            || pending.identity !== candidate.identity
            || pending.sequence > candidate.sequence
          )
        ) {
          clearTimer();
          startPendingSave();
          return;
        }

        if (retryAfterMs != null) {
          clearTimer();
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            startPendingSave();
          }, retryAfterMs);
          return;
        }

        if (
          continueWithPending
          || pending.generation !== candidate.generation
          || pending.identity !== candidate.identity
        ) {
          startPendingSave();
        }
      });
  }, [clearTimer]);

  const armDebounce = useCallback(() => {
    clearTimer();
    const generation = generationRef.current;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (generation !== generationRef.current) return;
      startPendingSave();
    }, debounceMsRef.current);
  }, [clearTimer, startPendingSave]);

  const schedule = useCallback(
    (state: SharedStudioState): boolean => {
      const currentIdentity = identityRef.current;
      if (currentIdentity == null) return false;

      const signature = getSharedStateSignature(state);
      const currentGeneration = generationRef.current;
      const pending = pendingRef.current;
      const inFlight = inFlightRef.current;
      const sequence = unscheduledSequenceRef.current
        ?? (nextSequenceRef.current + 1);
      unscheduledSequenceRef.current = null;
      nextSequenceRef.current = Math.max(nextSequenceRef.current, sequence);
      latestSequenceRef.current = sequence;

      if (
        pending
        && pending.generation === currentGeneration
        && pending.identity === currentIdentity
        && pending.signature === signature
      ) {
        if (pending.terminalFailure) {
          Object.assign(pending, registerProjectWrite(currentIdentity));
        }
        pending.state = state;
        pending.sequence = sequence;
        pending.retryAttempt = 0;
        pending.save = saveRef.current;
        pending.terminalFailure = false;
        if (!inFlight && !timerRef.current) armDebounce();
        return true;
      }

      if (
        !pending
        && inFlight
        && inFlight.generation === currentGeneration
        && inFlight.identity === currentIdentity
        && inFlight.signature === signature
      ) {
        pendingRef.current = {
          ...registerProjectWrite(currentIdentity),
          identity: currentIdentity,
          state,
          signature,
          sequence,
          generation: currentGeneration,
          retryAttempt: 0,
          save: saveRef.current,
        };
        return true;
      }

      if (!inFlight && signature === serverSignatureRef.current) {
        clearTimer();
        pending?.finish();
        pendingRef.current = null;
        callbacksRef.current.onPersisted?.(state);
        return false;
      }

      pendingRef.current = {
        ...(pending && !pending.terminalFailure
          ? { preceding: pending.preceding, finish: pending.finish }
          : registerProjectWrite(currentIdentity)),
        identity: currentIdentity,
        state,
        signature,
        sequence,
        generation: currentGeneration,
        retryAttempt: 0,
        save: saveRef.current,
      };
      if (!inFlight) armDebounce();
      return true;
    },
    [armDebounce, clearTimer],
  );

  const markIntent = useCallback(() => {
    const sequence = nextSequenceRef.current + 1;
    nextSequenceRef.current = sequence;
    latestSequenceRef.current = sequence;
    unscheduledSequenceRef.current = sequence;
  }, []);

  const setPersistedState = useCallback((state: SharedStudioState | null) => {
    const currentInFlight = inFlightRef.current;
    if (
      currentInFlight
      && currentInFlight.generation === generationRef.current
      && currentInFlight.identity === identityRef.current
    ) {
      return;
    }
    serverSignatureRef.current = state == null
      ? null
      : getSharedStateSignature(state);
  }, []);

  const flushDepartingState = useCallback(() => {
    clearTimer();
    const pending = pendingRef.current;
    if (pending && !pending.terminalFailure) departingRef.current.push(pending);
    pendingRef.current = null;
    startPendingSave();
  }, [clearTimer, startPendingSave]);

  useEffect(() => {
    generationRef.current += 1;
    flushDepartingState();
    identityRef.current = identity;
    latestSequenceRef.current = 0;
    unscheduledSequenceRef.current = null;
    serverSignatureRef.current = null;
    if (!inFlightRef.current && mountedRef.current) setIsPending(false);
  }, [flushDepartingState, identity]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      flushDepartingState();
    };
  }, [flushDepartingState]);

  return {
    isPending,
    markIntent,
    schedule,
    setPersistedState,
  };
}
